import React, { useEffect, useRef, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { actions as sessionActions, sessionSelector } from "reducers/session";
import { actions as notificationActions } from "reducers/notification";
import { actions, slotTemplateSelector } from "reducers/slotTemplateModal";
import { AppDispatch, RootState } from "store";
import { TemplateCreationModal } from "./TemplateCreationModal";
import { TemplateCreationModalContainerPropsType } from "./types";
import { NotificationStateType } from "reducers/types/notification";
import { TemplateFormValues } from "../types";
import { DATE_FORMATS } from "utils/constants";
import moment from "moment";
import "./i18n";
import { useTranslation } from "react-i18next";
import { dirtyAlert } from "utils/alerts";
import { FormRefType } from "utils/types";

const TemplateCreationModalContainer: React.FC<
  TemplateCreationModalContainerPropsType & ConnectorProps
> = (props): JSX.Element => {
  const { t } = useTranslation();
  const { onClose, isOpen } = props;
  const [values, setValues] = useState({} as TemplateFormValues);
  const formRef: FormRefType<TemplateFormValues> = useRef(null);
  const testId = "template-creation";

  const handleClose = () => {
    const close = () => {
      onClose();
      props.resetWorkzones();
      props.setDirty(false);
      props.setActualRegion("");
    };

    if (props.dirty) {
      props.showNotification(dirtyAlert({ onCancel: close, scroll: false }));
    } else {
      close();
    }
  };
  useEffect(() => {
    if (!isOpen && formRef.current) formRef.current?.restart();
  }, [isOpen]);

  const onUpdateWorkzones = (region: string) => {
    if (region && region !== props.actualRegion) {
      props.getWorkzones(region);
      props.setActualRegion(region);
    }
  };

  const onSubmit = (values: TemplateFormValues) => {
    props.getTemplateConfig();
    setValues(values);
  };

  useEffect(() => {
    if (props.launchDate && isOpen) {
      const [date, hour] = props.launchDate.split(" ");
      const formattedDate = moment(date, DATE_FORMATS.shortISODate).format(
        DATE_FORMATS.extraShortDate
      );
      props.showNotification({
        id: "notification-create-template",
        level: "info",
        title: t("templateCreation:createTemplateTitle"),
        body: t("templateCreation:createTemplateMessage", {
          date: formattedDate,
          time: hour.substring(0, 5),
        }),
        onConfirmLabel: "global:createConfirmation",
        onCancelLabel: "global:cancel",
        onConfirm: () => props.createTemplate(values),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.launchDate]);

  return (
    <TemplateCreationModal
      {...props}
      regions={props.regions}
      onClose={handleClose}
      onUpdateWorkzones={onUpdateWorkzones}
      formRef={formRef}
      onSubmit={onSubmit}
      data-testid={testId}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  dirty: sessionSelector(state).dirtyForm,
  workzones: slotTemplateSelector(state).workzones,
  actualRegion: slotTemplateSelector(state).actualRegion,
  launchDate: slotTemplateSelector(state).launchDate,
  fetchingWorkzones: slotTemplateSelector(state).fetchingWorkzones,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getWorkzones: (regionId: string) => {
    dispatch(actions.getWorkzonesFetch(regionId));
  },
  resetWorkzones: () => {
    dispatch(actions.resetWorkzones());
  },
  setActualRegion: (regionId: string) => {
    dispatch(actions.setActualRegion(regionId));
  },
  setDirty: (dirty: boolean) => {
    dispatch(sessionActions.setDirty(dirty));
  },
  showNotification: (content: NotificationStateType) => {
    dispatch(notificationActions.showNotification(content));
  },
  createTemplate: (values: TemplateFormValues) => {
    dispatch(actions.createTemplateRequest(values));
  },
  getTemplateConfig: () => {
    dispatch(actions.getTemplateConfigRequest());
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(TemplateCreationModalContainer);
